import { useState, useEffect, useRef } from "react";
import { MessageSquare, Lightbulb, Zap, Clock } from "lucide-react";

const defaultSteps = [
  { num: "01", title: "Tell Us What You Need", desc: "Tell us what you're looking to achieve, what you need help with, or what isn't currently working.", icon: MessageSquare },
  { num: "02", title: "We Recommend the Right Approach", desc: "We'll review your requirements and explain the most suitable service, options and costs.", icon: Lightbulb },
  { num: "03", title: "We Get It Sorted", desc: "We'll complete the agreed design, setup, improvement or support work and keep you updated along the way.", icon: Zap },
  { num: "04", title: "One-Off or Ongoing", desc: "Once the work is complete, you can leave it there or choose ongoing support and management where available.", icon: Clock },
];

export default function HorizontalTimeline({ steps, id, extraClass = "" }) {
  const s = steps || defaultSteps;
  const trackRef = useRef(null);
  const sectionRef = useRef(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) { setAnimated(true); if (sectionRef.current) sectionRef.current.classList.add("hp-revealed"); return; }
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setAnimated(true);
        if (sectionRef.current) sectionRef.current.classList.add("hp-revealed");
        obs.disconnect();
      }
    }, { threshold: 0.15 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id={id} data-testid="how-it-works" className={`hp-section py-20 md:py-28 px-6 md:px-12 lg:px-24 ${extraClass}`}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4">How It Works</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-extrabold leading-tight text-foreground mb-4">Simple From Start to Finish</h2>
          <p className="text-base md:text-lg text-muted-foreground">Whatever you need help with, we keep the process clear, straightforward and easy to follow.</p>
        </div>

        {/* Desktop horizontal */}
        <div ref={trackRef} className={`htl-track hidden md:flex ${animated ? "htl-animated" : ""}`}>
          <div className="htl-line"><div className="htl-line-progress" /></div>
          {s.map((step) => (
            <div key={step.num} className="htl-step" data-testid={`htl-step-${step.num}`}>
              <span className="htl-bg-num">{step.num}</span>
              <div className="htl-marker">{step.num}</div>
              <div className="htl-icon"><step.icon className="w-5 h-5" strokeWidth={1.75} /></div>
              <h3 className="htl-title">{step.title}</h3>
              <p className="htl-desc">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* Mobile vertical */}
        <div className="md:hidden relative pl-14">
          <div className="absolute left-[18px] top-0 bottom-0 w-[2px] bg-border" />
          <div className="space-y-8">
            {s.map((step) => (
              <div key={step.num} className="relative">
                <div className="absolute left-[-38px] w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-extrabold z-10">{step.num}</div>
                <h3 className="text-base font-bold text-foreground mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
